import type { HilPresentationRequestEnvelope } from '@bizfirst/hil-types';
import { HilPresentationType } from '@bizfirst/hil-types';

export interface ValidationResult<T> {
  ok: boolean;
  data?: T;
  errors: string[];
}

const KNOWN_TYPES: Set<string> = new Set(Object.values(HilPresentationType));

export class HilEnvelopeValidator {
  static validate(raw: unknown): ValidationResult<HilPresentationRequestEnvelope> {
    const errors: string[] = [];

    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return { ok: false, errors: ['Envelope must be a non-null object'] };
    }

    const env = raw as Record<string, unknown>;

    // Chat has no real EngageSession/assignees — Engage/actor-resolution is disabled for it
    // server-side (see BaseHilNodeExecutorSettings.HilFeatureInfo), so EngageSessionID stays 0,
    // ResumeCallbackURL stays empty, and Assignees stays []. Those are correct, not malformed —
    // only Form/Approval need a real session to route a reply through /api/hil/respond/{id}.
    const isChat = env['HilType'] === HilPresentationType.Chat;

    const session = env['Session'] as Record<string, unknown> | undefined;
    if (!session || typeof session !== 'object') {
      errors.push('Session is required');
    } else {
      if (typeof session['EngageSessionID'] !== 'number' || (!isChat && session['EngageSessionID'] <= 0)) {
        errors.push('Session.EngageSessionID must be a positive number');
      }
      if (!isChat && (!session['ResumeCallbackURL'] || typeof session['ResumeCallbackURL'] !== 'string')) {
        errors.push('Session.ResumeCallbackURL must be a non-empty string');
      }
      if (!session['CorrelationID'] || typeof session['CorrelationID'] !== 'string') {
        errors.push('Session.CorrelationID must be a non-empty string');
      }
    }

    if (!Array.isArray(env['Assignees']) || (!isChat && env['Assignees'].length === 0)) {
      errors.push('Assignees must be an array with at least one entry');
    }

    if (!Array.isArray(env['Presentations']) || env['Presentations'].length === 0) {
      errors.push('Presentations must be an array with at least one entry');
    } else {
      const presentations = env['Presentations'] as Record<string, unknown>[];
      for (let i = 0; i < presentations.length; i++) {
        const p = presentations[i];
        if (!p || typeof p['PresentationType'] !== 'string') {
          errors.push(`Presentations[${i}].PresentationType is required`);
        } else if (!KNOWN_TYPES.has(p['PresentationType'] as string)) {
          errors.push(`Presentations[${i}].PresentationType "${p['PresentationType']}" is not a known value`);
        }
      }
    }

    if (errors.length > 0) {
      return { ok: false, errors };
    }

    return { ok: true, data: raw as HilPresentationRequestEnvelope, errors: [] };
  }
}
